interface ProjectDocumentLead {
  id: string
}

/** The four business records a document may optionally link to. */
const RELATED_RECORD_TYPES = ["project", "proposal", "contract", "invoice"] as const

type RelatedRecordType = (typeof RELATED_RECORD_TYPES)[number]

interface RelatedRecord {
  type: RelatedRecordType
  id: string
  label: string
}

interface ProjectDocument {
  id: string
  lead: ProjectDocumentLead
  file_name: string
  file: string
  // A label string on list, the stored integer everywhere else.
  file_type: number | string | null
  file_type_name: string | null
  uploaded_by: string | null
  created: string
  updated: string
  version: number
  is_current?: boolean
  revision_note?: string | null
  file_size?: number | null
  formatted_file_size?: string | null
  folder?: string | null
  // Business classification — independent of `file_type` above, both optional.
  document_type?: string | null
  document_type_label?: string | null
  // The workflow status of the logical document — belongs to whichever
  // version is current, `null` for the (large majority of) files that never
  // opt into a workflow. Changed only via `changeDocumentStatus`.
  status?: string | null
  status_label?: string | null
  related_record?: RelatedRecord | null
}

interface ProjectDocumentsParams {
  lead: string
  page?: number
  page_size?: number
  // DRF's SEARCH_PARAM is renamed to `q`; `search` is silently ignored.
  q?: string
  folder?: string
  folder__isnull?: boolean
  document_type?: string
  related_record_type?: RelatedRecordType
  related_record_id?: string
}

interface ProjectDocumentMoveData {
  folder: string | null
}

/** `null` on either pair clears the link; omitting a field leaves it alone. */
interface ProjectDocumentClassificationData {
  document_type?: string | null
  related_record_type?: RelatedRecordType | null
  related_record_id?: string | null
}

interface ProjectDocumentTypeOption {
  label: string
  value: number
  // Draft is filter-only: the bucket untyped uploads land in, never a choice.
  visible_in: Array<"filter" | "form">
}

interface ProjectDocumentFilterOptions {
  file_type: ProjectDocumentTypeOption[]
}

interface ProjectDocumentUploadData {
  lead: string
  file_name: string
  file: File
  file_type?: number
  folder?: string
  document_type?: string
  // Set to upload a new version of an existing document rather than a
  // brand-new one; file-level metadata omitted here is carried forward from
  // the version this points at.
  previous_version_id?: string
  revision_note?: string
}

export { RELATED_RECORD_TYPES }
export type {
  ProjectDocument,
  ProjectDocumentClassificationData,
  ProjectDocumentFilterOptions,
  ProjectDocumentLead,
  ProjectDocumentMoveData,
  ProjectDocumentTypeOption,
  ProjectDocumentUploadData,
  ProjectDocumentsParams,
  RelatedRecord,
  RelatedRecordType
}
